import { readCoordinatePair, readOrderCoordinate } from './mapCoordinates';

// Placeholder Las Vegas Valley bounds for the offline dispatch surface. These
// are not tied to a production map asset yet; the final image/provider decision
// will set the durable bounds used by the packaged offline map.
export const LAS_VEGAS_OFFLINE_MAP_BOUNDS = {
  north: 36.45,
  south: 35.85,
  west: -115.55,
  east: -114.85,
};

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const mercatorY = (lat) => {
  const rad = (lat * Math.PI) / 180;
  return Math.log(Math.tan(Math.PI / 4 + rad / 2));
};

const latitudeFromMercatorY = (y) => (
  ((2 * Math.atan(Math.exp(y))) - (Math.PI / 2)) * 180 / Math.PI
);

export const offlineCoordinateForOrder = (order) => {
  if (!order) return null;
  const localOrder = order.desktop_sync?.origin === 'local';

  const offlineMap = order.desktop_sync?.offline_map || {};
  const authoritative = !localOrder && readOrderCoordinate(order);
  if (authoritative) {
    return {
      ...authoritative,
      source: 'authoritative',
    };
  }

  // A locally placed order must meet the backend's numeric placement contract.
  // Cached cloud coordinates retain the existing numeric-string read support.
  const placed = readCoordinatePair(offlineMap.latitude, offlineMap.longitude, {
    allowNumericStrings: !localOrder,
  });
  if (placed) {
    return {
      ...placed,
      source: offlineMap.source || 'offline_map',
    };
  }

  return null;
};

export const projectOfflineCoordinate = (
  coordinate,
  bounds = LAS_VEGAS_OFFLINE_MAP_BOUNDS,
) => {
  if (!coordinate) return null;

  const latitude = Number(coordinate.latitude);
  const longitude = Number(coordinate.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null;

  const inside =
    latitude <= bounds.north &&
    latitude >= bounds.south &&
    longitude >= bounds.west &&
    longitude <= bounds.east;

  const x = ((longitude - bounds.west) / (bounds.east - bounds.west)) * 100;
  const northY = mercatorY(bounds.north);
  const southY = mercatorY(bounds.south);
  const y = ((northY - mercatorY(latitude)) / (northY - southY)) * 100;

  return {
    xPercent: clamp(x, 0, 100),
    yPercent: clamp(y, 0, 100),
    inside,
  };
};

export const unprojectOfflinePoint = (
  point,
  bounds = LAS_VEGAS_OFFLINE_MAP_BOUNDS,
) => {
  if (!point) return null;

  const xPercent = Number(point.xPercent);
  const yPercent = Number(point.yPercent);
  if (!Number.isFinite(xPercent) || !Number.isFinite(yPercent)) return null;

  const clampedX = clamp(xPercent, 0, 100);
  const clampedY = clamp(yPercent, 0, 100);
  const longitude = bounds.west + (clampedX / 100) * (bounds.east - bounds.west);
  const northY = mercatorY(bounds.north);
  const southY = mercatorY(bounds.south);
  const projectedY = northY - (clampedY / 100) * (northY - southY);

  return {
    latitude: latitudeFromMercatorY(projectedY),
    longitude,
  };
};
